import { useState, useTransition } from 'react';

import cx from 'classnames';

import { datadogLogs } from '@datadog/browser-logs';

import { Button, InputAuth } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';
import { VRM } from '@motorway/mw-highway-code/alpha';

import { AgentsBox, INSPECTION_STATUSES, PHONE_NUMBERS, wsEventBus, WS_EVENTS } from '@motorway/motorway-handshake-components';
import { useFetch } from '@motorway/motorway-handshake-components';
import { useVehicleLookup } from '@motorway/motorway-handshake-components';

import { useAuth } from '../../api/Auth';
import { Header } from '../../components/Header/Header';
import { Logo } from '../../components/Logo/Logo';
import { useDriver } from '../../contexts/Driver';
import { ROUTES } from '../../helpers/routes';
import { snowplowCtaClick } from '../../helpers/snowplow';

import type { RedeemResponse } from '../../@types/Redeem';

import styles from './OTPCode.module.scss';

export const OTPCode = () => {
  const [apiSuccess, setApiSuccess] = useState<boolean | null>(null);

  const { driver } = useDriver();
  const { vehicle } = useVehicleLookup();
  const { enquiry } = vehicle ?? {};

  const { redeem } = useFetch();
  const { signInDriver } = useAuth();
  const [isPending, startTransition] = useTransition();

  const PRETTY_VRM = enquiry?.vehicleDetails.prettyVrm;

  return (
    <>
      <div className={styles.component}>
        <Logo />
        {
          PRETTY_VRM && (
            <div className={styles.vrm}>
              <VRM size="small" text={PRETTY_VRM} />
            </div>
          )
        }
        <Header>
          <h1>Enter your collection PIN to start collection</h1>
        </Header>
        <div className={cx(styles.otp, { [styles.pending]: isPending })}>
          <InputAuth
            length={4}
            id="dealer-otp"
            onFill={({ onSuccess, onError, value }) => {
              setApiSuccess(null);

              startTransition(async () => {
                const { success, data } = await redeem({
                  code: driver?.code ?? '',
                  pin: value,
                }) as { success: boolean; data: RedeemResponse };

                snowplowCtaClick({
                  name: 'dealer_pin_submitted',
                  button_label: 'Submit pin',
                  success,
                  url: ROUTES.INSPECTION,
                });

                const vector = (success)
                  ? 'info'
                  : 'error';

                datadogLogs.logger[vector]('Dealer PIN result', {
                  code: (success) ? 'Success' : driver?.code ?? 'N/A',
                  success,
                  value: (success) ? 'Success' : value,
                });

                if (success) {
                  onSuccess();
                  setApiSuccess(true);

                  setTimeout(() => {
                    signInDriver(data);

                    wsEventBus.dispatchEvent(new CustomEvent(WS_EVENTS.STATUS_CHANGED, {
                      detail: { status: INSPECTION_STATUSES.REDEEMED },
                    }));
                  }, 400);
                  return;
                }

                setApiSuccess(false);
                onError();
              });
            }}
          />
        </div>
        <div className={styles.status}>
          {isPending && apiSuccess === null && <LoadingIndicator />}
          {apiSuccess === true && <span className={styles.success}>Pin accepted! Redirecting...</span>}
          {apiSuccess === false && <span className={styles.error}>Invalid pin, please try again.</span>}
        </div>
      </div>
      <div className={styles.agentsBox}>
        <AgentsBox />
        <div className={styles.agentsBoxContent}>
          <div>
            <h2>Need help starting?</h2>
            <p>Speak to our driveway support team.</p>
          </div>
          <Button
            as="a"
            href={`tel:${PHONE_NUMBERS.RAW}`}
            label="Call us"
            onClick={() => {
              snowplowCtaClick({
                name: 'support_clicked',
                button_label: 'Call us',
                url: PHONE_NUMBERS.RAW,
              });
            }}
            showLabel
            size="small"
            type="reset"
            variant="secondary"
          />
        </div>
      </div>
    </>
  );
};
